// Shared OTP form — extracted from LoginScreen for reuse by the
// session-expired modal (#294). Owns step/email/code/error/loading state,
// calls signInWithOtp + verifyOtp from AuthContext, and reports the verified
// (accessToken, email) to the caller via onSuccess. (verifyOtp also writes
// the new credentials into auth state itself; onSuccess lets each caller —
// login navigation vs modal resume — finish its own half of the flow.)
import { useState, useEffect, useRef } from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { useAuth } from '../../contexts/AuthContext';

// A requested code stays valid for 15 min server-side; a short client-side cooldown
// keeps users from hammering "Resend" (repeated resends are what trip the server's
// send throttle in the first place).
const RESEND_COOLDOWN_SECONDS = 30;

interface OtpFormProps {
  initialEmail?: string;
  onSuccess: (accessToken: string, email: string) => void;
  onCancel?: () => void;
  cancelLabel?: string;
  /**
   * When true (the session-expired modal), the email is bound to the expired
   * identity: the field is read-only and the "different email" escape hatch
   * is hidden, so parked requests can never be replayed under a different
   * user's fresh token. Login leaves this false.
   */
  emailLocked?: boolean;
}

const LINK_BUTTON =
  'text-sm text-amber hover:text-amber-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded py-1 px-2 -mx-2';
const CANCEL_BUTTON =
  'text-sm text-gray-400 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded py-1 px-2 -mx-2 disabled:opacity-50 disabled:cursor-not-allowed';

export function OtpForm({ initialEmail = '', onSuccess, onCancel, cancelLabel, emailLocked = false }: OtpFormProps) {
  const { signInWithOtp, verifyOtp } = useAuth();

  // Always start at the email step, even when an initial email is prefilled
  // (the session-expired modal case). Jumping straight to the code step would
  // show a code box for a code that was never sent — the user must explicitly
  // request one via Send Code first.
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCodeHint, setShowCodeHint] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const resendDeadline = useRef(0);
  const hintTimer = useRef<ReturnType<typeof setTimeout>>();
  const mockMode = import.meta.env.VITE_MOCK === '1';

  useEffect(() => {
    if (step === 'code') {
      setShowCodeHint(false);
      hintTimer.current = setTimeout(() => setShowCodeHint(true), 60000);
      return () => clearTimeout(hintTimer.current);
    }
  }, [step]);

  // Use elapsed wall time: sleeping/background tabs can skip timer callbacks.
  useEffect(() => {
    if (cooldown <= 0) return;
    const updateCooldown = () => setCooldown(Math.max(0,
      Math.ceil((resendDeadline.current - Date.now()) / 1000),
    ));
    updateCooldown();
    const t = setTimeout(updateCooldown, 1000);
    window.addEventListener('focus', updateCooldown);
    document.addEventListener('visibilitychange', updateCooldown);
    return () => {
      clearTimeout(t);
      window.removeEventListener('focus', updateCooldown);
      document.removeEventListener('visibilitychange', updateCooldown);
    };
  }, [cooldown]);

  const handleSendCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // The submit Button disables on click while loading, but Enter inside the
    // input submits the form regardless — guard here so a fast double-Enter
    // can't fire a second send (the easiest way to trip the server throttle).
    if (!email || cooldown > 0 || loading) return;
    setLoading(true);
    setError('');
    try {
      const { error: authError } = await signInWithOtp(email);
      if (authError) {
        setError(authError.message);
      } else {
        setStep('code');
        setCode('');
        resendDeadline.current = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
        setCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Dev-mock only: skip the OTP dance entirely.
  const handleSkipLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const demoEmail = email || 'demo@geniac.io';
      const result = await verifyOtp(demoEmail, '000000');
      if (result.error) setError(result.error.message);
      else if (result.access_token && result.email) onSuccess(result.access_token, result.email);
      else setError('Something went wrong');
    } catch (err: any) {
      setError(err.message || 'Skip failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    // Same Enter-while-loading guard as handleSendCode: one verify in flight
    // at a time, so "Sign out instead" can't race a late success either.
    if (loading || !code || code.length < 6) return;
    setLoading(true);
    setError('');
    try {
      const result = await verifyOtp(email, code);
      if (result.error) {
        setError(
          result.error.message === 'Request was cancelled'
            ? 'Sign-in was cancelled.'
            : result.error.message,
        );
      } else if (result.access_token && result.email) {
        onSuccess(result.access_token, result.email);
      } else {
        setError('Something went wrong');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {step === 'email' ? (
        <form onSubmit={handleSendCode} className="space-y-6">
          {error && (
            <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}
          <div>
            <Input
              type="email"
              autoComplete="email"
              label="Email"
              aria-label="Email"
              placeholder="Enter your email"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              required
              // Standalone login keeps native autoFocus. Inside the modal the
              // focus trap owns initial focus — and it must observe the
              // pre-modal element to restore focus correctly on close, which
              // an autoFocused input would already have displaced.
              autoFocus={!emailLocked}
              readOnly={emailLocked || loading}
            />
          </div>
          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Sending...' : 'Send Code'}
          </Button>
          <p className="text-sm text-gray-400 text-center">
            We&apos;ll send a 6-digit verification code to your email.
          </p>
          {onCancel && (
            <div className="text-center pt-2 border-t border-navy-lighter">
              <button
                type="button"
                onClick={onCancel}
                className={CANCEL_BUTTON}
              >
                {cancelLabel ?? 'Cancel'}
              </button>
            </div>
          )}
          {mockMode && !emailLocked && (
            <div className="text-center pt-2 border-t border-navy-lighter">
              <button
                type="button"
                onClick={handleSkipLogin}
                disabled={loading}
                className="text-sm text-amber hover:text-amber-hover transition-colors"
              >
                Skip login (dev mock)
              </button>
            </div>
          )}
        </form>
      ) : (
        <form onSubmit={handleVerifyCode} className="space-y-6">
          {error && (
            <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}
          <div>
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              label="Verification code"
              aria-label="Verification code"
              placeholder="000000"
              value={code}
              onChange={(e: any) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              autoFocus
              aria-describedby="otp-sent-to"
              inputClassName="text-center text-2xl tracking-widest font-mono"
            />
          </div>
          <Button type="submit" fullWidth size="lg" disabled={loading || code.length < 6}>
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
          <div className="text-center space-y-2">
            <p id="otp-sent-to" className="text-sm text-gray-400">
              Enter the code sent to <span className="text-white font-medium">{email}</span>
            </p>
            {!emailLocked && (
              <>
                <button
                  type="button"
                  className={LINK_BUTTON}
                  disabled={loading}
                  onClick={() => { setStep('email'); setCode(''); setError(''); resendDeadline.current = 0; setCooldown(0); }}
                >
                  Use a different email
                </button>
                <span className="text-navy-muted mx-2">·</span>
              </>
            )}
            <button
              type="button"
              disabled={loading || cooldown > 0}
              className={`${LINK_BUTTON} disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-amber`}
              onClick={() => handleSendCode()}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
          {onCancel && (
            <div className="text-center pt-2 border-t border-navy-lighter">
              <button
                type="button"
                onClick={onCancel}
                className={CANCEL_BUTTON}
              >
                {cancelLabel ?? 'Cancel'}
              </button>
            </div>
          )}
          {showCodeHint && (
            <p className="text-xs text-gray-400 text-center mt-3">
              Didn&apos;t get a code? Check your spam folder. If you requested more than one,
              only your most recent code will work. Contact your administrator if you
              don&apos;t have access.
            </p>
          )}
        </form>
      )}
    </>
  );
}
